import { useState } from 'react';
import { Plus, X, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePresetContentWarnings } from '@/hooks/usePresetContentWarnings';
import { useBookContentWarnings } from '@/hooks/useBookContentWarnings';

interface ContentWarningManagerProps {
  bookId: string;
}

export function ContentWarningManager({ bookId }: ContentWarningManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customWarning, setCustomWarning] = useState('');

  const { warningsByCategory, sortedCategories, categoryLabels, isLoading: presetsLoading } = usePresetContentWarnings();
  const { userWarnings, addWarning, removeWarning, isAdding } = useBookContentWarnings(bookId);

  const userWarningNames = new Set(userWarnings.map(w => w.warning_name));

  const handleToggleWarning = (warningName: string) => {
    if (userWarningNames.has(warningName)) {
      removeWarning(warningName);
    } else {
      addWarning({ warningName });
    }
  };

  const handleAddCustom = () => {
    if (customWarning.trim() && !userWarningNames.has(customWarning.trim())) {
      addWarning({ warningName: customWarning.trim() });
      setCustomWarning('');
    }
  };

  const filteredCategories = sortedCategories.map(category => ({
    category,
    label: categoryLabels[category],
    warnings: warningsByCategory[category]?.filter(w =>
      w.warning_name.toLowerCase().includes(search.toLowerCase())
    ) || [],
  })).filter(c => c.warnings.length > 0);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          Add Warning
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search warnings..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        <ScrollArea className="h-[300px]">
          <div className="p-3 space-y-4">
            {/* User's current warnings */}
            {userWarnings.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2">Your warnings</h4>
                <div className="flex flex-wrap gap-1.5">
                  {userWarnings.map((warning) => (
                    <Badge
                      key={warning.id}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive/20 gap-1"
                      onClick={() => removeWarning(warning.warning_name)}
                    >
                      {warning.warning_name}
                      <X className="h-3 w-3" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Preset warnings by category */}
            {presetsLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : (
              filteredCategories.map(({ category, label, warnings }) => (
                <div key={category}>
                  <h4 className="text-xs font-medium text-muted-foreground mb-2">{label}</h4>
                  <div className="space-y-1">
                    {warnings.map((warning) => {
                      const isSelected = userWarningNames.has(warning.warning_name);
                      return (
                        <button
                          key={warning.id}
                          onClick={() => handleToggleWarning(warning.warning_name)}
                          disabled={isAdding}
                          className={`
                            w-full text-left px-2 py-1.5 rounded-md text-sm
                            flex items-center justify-between gap-2
                            transition-colors
                            ${isSelected
                              ? 'bg-primary/10 text-primary'
                              : 'hover:bg-muted'
                            }
                          `}
                        >
                          <span>{warning.warning_name}</span>
                          {isSelected && <Check className="h-4 w-4" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}

            {/* Custom warning input */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Add custom warning</h4>
              <div className="flex gap-2">
                <Input
                  placeholder="Custom warning..."
                  value={customWarning}
                  onChange={(e) => setCustomWarning(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustom();
                    }
                  }}
                  className="text-sm"
                />
                <Button
                  size="sm"
                  onClick={handleAddCustom}
                  disabled={!customWarning.trim() || isAdding}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
